import React, { CSSProperties, createRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { PartyBarData } from 'lib/PartyBar/partybar.types';

interface Props {
  lists: PartyBarData[];
  hoveredListId: number;
  setHoveredListId: (id: number) => void;
}

const CIRCLE_DEGREE = 240;
const TOOLTIP_OFFSET = 20;

const SeatsChartDonut = ({ lists, hoveredListId, setHoveredListId }: Props) => {
  const { t } = useTranslation('wap');
  const seatchartRef = createRef<HTMLDivElement>();
  const [mousePosition, setMousePosition] = useState(null);

  const totalSeatsClaimed = lists.reduce(
    (acc, { seatsClaimed }) => acc + seatsClaimed,
    0,
  );

  let totalDegree = 0;

  const chartSegments = lists.map((list) => {
    const { color, seatsClaimed } = list;
    const degree = (seatsClaimed / totalSeatsClaimed) * CIRCLE_DEGREE;

    const offset = totalDegree;
    totalDegree += degree;

    return {
      color,
      id: list.listId,
      degree,
      offset,
    };
  });

  const hoveredSegment = lists.find(({ listId }) => listId === hoveredListId);

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const { left, top } = seatchartRef.current.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;

    setMousePosition({ x, y });
  };

  return (
    <div className="seatchart" aria-hidden ref={seatchartRef}>
      <div className="seatchart__inner">
        <ol className="seatchart__items">
          {chartSegments.map(({ degree, offset, id, color }) => (
            <li
              key={id}
              className={classNames('seatchart__item', {
                'seatchart__item--hovered': id === hoveredListId,
                'seatchart__item--pale': hoveredListId !== null && id !== hoveredListId,
              })}
              style={{ '--rotation': `${offset}deg` } as CSSProperties}
            >
              <div
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                onFocus={() => setHoveredListId(id)}
                onBlur={() => setHoveredListId(null)}
                onMouseEnter={() => setHoveredListId(id)}
                onMouseLeave={() => {
                  setHoveredListId(null);
                  setMousePosition(null);
                }}
                onMouseMove={onMouseMove}
                className="seatchart__circle"
                style={
                  { '--color': color, '--rotation': `${degree}deg` } as CSSProperties
                }
              />
            </li>
          ))}
        </ol>
        <h2 className="seatchart__center h2">
          <span>{totalSeatsClaimed}</span>
          <span>{t('statistics_seats')}</span>
        </h2>
      </div>
      {hoveredSegment && mousePosition && (
        <div
          className="seatchart__tooltip"
          style={{
            top: mousePosition.y + TOOLTIP_OFFSET,
            left: mousePosition.x + TOOLTIP_OFFSET,
          }}
        >
          <span className="seatchart__legend-item-list">{hoveredSegment.shortTitle}</span>
          <span className="seatchart__legend-item-seats">
            {t('seats', { count: hoveredSegment.seatsClaimed })}
          </span>
        </div>
      )}
    </div>
  );
};

export default SeatsChartDonut;
